import { useEffect, useState } from 'react';
import { Box, InputBase, IconButton, Badge, Chip, Avatar, Typography } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import { useSelector } from 'react-redux';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { logout, fetchCurrentUser } from '../../services/api/authService';
import { tokens } from '../../theme/theme';

function scoreColor(score) {
  if (score >= 80) return tokens.severity.low;
  if (score >= 60) return tokens.severity.medium;
  return tokens.severity.critical;
}

function initialsOf(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
}

export default function Topbar({ onMenu }) {
  const navigate = useNavigate();
  const score = useSelector((s) => s.dashboard.score);
  const incidents = useSelector((s) => s.dashboard.incidents);
  const openIncidents = incidents?.filter((i) => i.status !== 'contained' && i.status !== 'resolved').length ?? 0;
  const [me, setMe] = useState(null);

  useEffect(() => { fetchCurrentUser().then(setMe).catch(() => setMe(null)); }, []);

  return (
    <Box
      sx={{
        height: 64,
        px: { xs: 1, sm: 2, md: 3 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${tokens.border}`,
        backgroundColor: tokens.bg.default
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <IconButton onClick={onMenu} aria-label="Open navigation" sx={{ display: { md: 'none' } }}><MenuRoundedIcon /></IconButton>
        <Box
        sx={{
          display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1,
          backgroundColor: tokens.bg.surface, border: `1px solid ${tokens.border}`,
          borderRadius: 2, px: 1.5, py: 0.6, width: 360
        }}
      >
        <SearchRoundedIcon fontSize="small" sx={{ color: tokens.text.muted }} />
        <InputBase
          aria-label="Global security search"
          placeholder="Search IPs, incidents, assets, CVEs…"
          sx={{ fontSize: '0.85rem', flex: 1, color: tokens.text.primary, fontFamily: '"JetBrains Mono", monospace' }}
        />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: .5, sm: 1, md: 2 } }}>
        {score && (
          <Chip
            label={`Security Score ${score.score}`}
            sx={{
              color: scoreColor(score.score),
              border: `1px solid ${scoreColor(score.score)}`,
              backgroundColor: `${scoreColor(score.score)}1A`,
              fontWeight: 600
            }}
            size="small"
          />
        )}
        <IconButton aria-label={`Notifications: ${openIncidents} open incidents`} onClick={() => navigate('/notifications')}>
          <Badge badgeContent={openIncidents} color="error">
            <NotificationsRoundedIcon sx={{ color: tokens.text.secondary }} />
          </Badge>
        </IconButton>
        <Box
          sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => navigate('/account')}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: tokens.brand.dim, fontSize: '0.8rem' }}>{initialsOf(me?.fullName)}</Avatar>
          <Box sx={{ lineHeight: 1.1 }}>
            <Typography variant="body2" sx={{ color: tokens.text.primary, fontWeight: 600, fontSize: '0.8rem' }}>
              {me?.fullName || '—'}
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.text.muted }}>
              {(me?.roles?.[0] || '').replaceAll('_', ' ') || '—'}
            </Typography>
          </Box>
        </Box>
        <Button size="small" sx={{ display: { xs: 'none', sm: 'inline-flex' } }} onClick={async () => { await logout(); navigate('/login', { replace: true }); }}>
          Sign out
        </Button>
      </Box>
    </Box>
  );
}
